import { CheckCircle2, CircleHelp, Code2, Shield, Users } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import FreshpaintLogoLight from "@/components/FpLogoLight";
import { LoadingSpinner } from "@/design-system/components/LoadingSpinner/LoadingSpinner";
import PageWithTitle from "@/PageWithTitle";
import Risk from "@/trackers/Risk";

import { fetchPublicTrackerDetails, type PublicTrackerDetails } from "./api";

const loginPath = (trackerKey: string) => {
  const next = `/monitoring/trackingTools/${encodeURIComponent(trackerKey)}`;
  return `/login?${new URLSearchParams({ next }).toString()}`;
};

function DetailCard({
  icon,
  title,
  children,
  className = "",
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={`border-borderSubtle flex flex-col gap-2 rounded-md border bg-gray-50 p-4 ${className}`}
    >
      <div className="flex items-center gap-2">
        <span className="text-blue-600">{icon}</span>
        <h2 className="text-textDefault text-sm font-semibold">{title}</h2>
      </div>
      <div className="text-textMediumEmphasis text-sm leading-5">
        {children}
      </div>
    </section>
  );
}

function GuidanceText({
  children,
  alternative,
  trackerKey,
}: {
  children: string;
  alternative: PublicTrackerDetails["guidance"]["alternative"];
  trackerKey: string;
}) {
  if (!alternative) {
    return <>{children}</>;
  }

  const alternativeIndex = children.indexOf(alternative.name);
  if (alternativeIndex < 0) {
    return <>{children}</>;
  }

  return (
    <>
      {children.slice(0, alternativeIndex)}
      <a
        className="cursor-pointer font-semibold text-blue-700 underline decoration-blue-300 underline-offset-2 hover:text-blue-900"
        href={loginPath(trackerKey)}
      >
        {alternative.name}
      </a>
      {children.slice(alternativeIndex + alternative.name.length)}
    </>
  );
}

function TrackerDetails({ details }: { details: PublicTrackerDetails }) {
  const whoCanFixIt = details.guidance.whoCanFixIt.trim();
  const domains = details.domains.slice(0, 4);
  const hiddenDomainCount = details.domains.length - domains.length;

  return (
    <div className="w-full max-w-5xl">
      <article className="border-borderSubtle overflow-hidden rounded-xl border bg-white shadow-sm">
        <div
          className="flex flex-col items-start px-5 py-5 text-left"
          data-testid="tracker-summary"
        >
          <Risk
            riskLevel={details.riskLevel}
            description={details.description}
            infoMode="none"
          />
          <h1 className="text-textDefault mt-2 text-xl font-semibold tracking-tight">
            {details.trackerName}
          </h1>
        </div>

        <div
          className={
            whoCanFixIt
              ? "border-borderSubtle grid grid-cols-1 gap-3 border-t p-4 sm:p-6 lg:grid-cols-3"
              : "border-borderSubtle grid grid-cols-1 gap-3 border-t p-4 sm:p-6 lg:grid-cols-2"
          }
          data-testid="tracker-guidance-grid"
        >
          <DetailCard
            title="Why it’s flagged"
            icon={<Shield className="h-4 w-4" aria-hidden />}
          >
            {details.guidance.whyFlagged}
          </DetailCard>
          <DetailCard
            title="Common fixes"
            icon={<CheckCircle2 className="h-4 w-4" aria-hidden />}
          >
            <GuidanceText
              alternative={details.guidance.alternative}
              trackerKey={details.trackerKey}
            >
              {details.guidance.whatToDo}
            </GuidanceText>
          </DetailCard>
          {whoCanFixIt && (
            <DetailCard
              title="Who can fix it"
              icon={<Users className="h-4 w-4" aria-hidden />}
            >
              {whoCanFixIt}
            </DetailCard>
          )}
          <DetailCard
            title="Common tracker domains"
            icon={<Code2 className="h-4 w-4" aria-hidden />}
            className={whoCanFixIt ? "lg:col-span-3" : "lg:col-span-2"}
          >
            {domains.length > 0 ? (
              <ul className="flex flex-wrap gap-2" aria-label="Tracker domains">
                {domains.map((domain) => (
                  <li
                    className="rounded-md border border-gray-200 bg-white px-2.5 py-1 font-mono text-xs text-gray-700"
                    key={domain}
                  >
                    {domain}
                  </li>
                ))}
                {hiddenDomainCount > 0 && (
                  <li className="rounded-md bg-gray-200 px-2.5 py-1 text-xs font-medium text-gray-600">
                    +{hiddenDomainCount} more
                  </li>
                )}
              </ul>
            ) : (
              `This ${details.category || "third-party"} tracker is typically added through site code or a tag manager.`
            )}
          </DetailCard>
        </div>

        <section className="border-borderSubtle flex flex-col gap-4 border-t bg-gray-50 px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-8">
          <div>
            <h2 className="text-textDefault text-sm font-semibold">
              Find this tracker on your sites
            </h2>
            <p className="text-textSubtle mt-1 text-sm">
              Sign in to see affected pages, activity, and review status.
            </p>
          </div>
          <a
            href={loginPath(details.trackerKey)}
            className="inline-flex cursor-pointer items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold whitespace-nowrap text-white shadow-sm hover:bg-blue-700"
          >
            Sign in to Freshpaint
          </a>
        </section>
      </article>
    </div>
  );
}

function PublicTrackerDetailPage() {
  const { trackerKey = "" } = useParams<{ trackerKey: string }>();
  const [details, setDetails] = useState<PublicTrackerDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    setIsError(false);

    fetchPublicTrackerDetails(trackerKey)
      .then((result) => {
        if (active) {
          setDetails(result);
        }
      })
      .catch(() => {
        if (active) {
          setIsError(true);
        }
      })
      .finally(() => {
        if (active) {
          setIsLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [trackerKey]);

  const title = details?.trackerName ?? "Tracker details";

  return (
    <PageWithTitle title={title}>
      <div className="min-h-screen bg-gray-50">
        <header className="bg-slate-900">
          <div className="mx-auto flex max-w-5xl flex-col items-start gap-2 px-4 py-4 sm:px-6 md:flex-row md:items-center md:justify-between">
            <FreshpaintLogoLight
              className="h-auto w-28"
              aria-label="Freshpaint"
            />
            <p className="text-xs leading-5 text-gray-300">
              A tracker was shared from Freshpaint Web Tracker Monitoring.
            </p>
          </div>
        </header>
        <main className="flex justify-center px-4 py-8 sm:px-6 sm:py-12">
          {isLoading && (
            <div className="flex min-h-80 items-center justify-center">
              <LoadingSpinner />
            </div>
          )}
          {!isLoading && isError && (
            <section className="border-borderSubtle mx-auto flex max-w-xl flex-col items-center gap-3 rounded-lg border bg-white p-8 text-center shadow-sm">
              <CircleHelp
                className="text-iconMinimal h-8 w-8"
                strokeWidth={1.5}
                aria-hidden
              />
              <h1 className="text-textDefault text-xl font-semibold">
                Tracker details are unavailable
              </h1>
              <p className="text-textSubtle text-sm">
                Check the link and try again.
              </p>
            </section>
          )}
          {!isLoading && !isError && details && (
            <TrackerDetails details={details} />
          )}
        </main>
      </div>
    </PageWithTitle>
  );
}

export default PublicTrackerDetailPage;
